// frontend/src/utils/AuthContext.jsx
// Provides Supabase auth state to the whole app.
// Also fetches the business row for the logged-in user.

import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from './supabase';

const AuthContext = createContext({});

const DEMO_BUSINESS = {
  id: 'demo-biz-123',
  user_id: 'demo-user-123',
  name: 'Wafiyo Cafe',
  slug: 'wafiyo-cafe',
  bg_color: '#1A7A4A',
  text_color: '#FFFFFF',
  logo_url: null,
  header_mode: 'gradient',
  subscription_status: 'active',
  created_at: new Date().toISOString()
};

const DEMO_SESSION = {
  user: {
    id: 'demo-user-123',
    email: 'demo@wafiyo.com',
    email_confirmed_at: new Date().toISOString()
  }
};

export function AuthProvider({ children }) {
  const location = useLocation();

  // Synchronously check demo mode before initial render to prevent flash-redirects
  const isDemoInitial = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('demo') === 'true' ||
      localStorage.getItem('wafiyo_demo_mode') === 'true');

  const [session, setSession] = useState(isDemoInitial ? DEMO_SESSION : null);
  const [business, setBusiness] = useState(isDemoInitial ? DEMO_BUSINESS : null);
  const [loading, setLoading] = useState(!isDemoInitial); // Bypass loading if demo
  const [isDemo, setIsDemo] = useState(isDemoInitial);

  // Listen for the demo flag in the URL even during client-side navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasDemoFlag = urlParams.get('demo') === 'true';

    if (hasDemoFlag) {
      localStorage.setItem('wafiyo_demo_mode', 'true');
      setIsDemo(true);
      setSession(DEMO_SESSION);
      setBusiness(DEMO_BUSINESS);
      setLoading(false);
      // Clean URL cleanly without triggering a reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search]);

  useEffect(() => {
    // Normal flow: Get initial session
    if (localStorage.getItem('wafiyo_demo_mode') === 'true') {
      setIsDemo(true);
      setSession(DEMO_SESSION);
      setBusiness(DEMO_BUSINESS);
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        setLoading(true);
        fetchBusiness(session.user.id);
        if (window.posthog) {
          window.posthog.identify(session.user.id, {
            email: session.user.email,
          });
        }
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (localStorage.getItem('wafiyo_demo_mode') === 'true') return; // Ignore real auth events if in demo

      setSession(session);
      if (session) {
        setLoading(true);
        fetchBusiness(session.user.id);
        if (window.posthog) {
          window.posthog.identify(session.user.id, {
            email: session.user.email,
          });
        }
      } else {
        setBusiness(null);
        setLoading(false);
        if (window.posthog) {
          window.posthog.reset();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchBusiness(userId) {
    if (isDemo || localStorage.getItem('wafiyo_demo_mode') === 'true') return;

    try {
      const { data } = await supabase
        .from('businesses')
        .select('*')
        .eq('user_id', userId)
        .single();
      setBusiness(data);
    } catch (err) {
      console.error('[Auth] Error fetching business:', err);
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    localStorage.removeItem('wafiyo_demo_mode');

    if (isDemo) {
      // Direct redirect to register to avoid intermediate state updates 
      // which might trigger protected route redirects to /login.
      supabase.auth.signOut();
      window.location.href = '/register';
      return;
    }

    await supabase.auth.signOut();
  }

  // Support local mutations for demo mode (e.g., updating card color instantly)
  const setDemoBusiness = (updates) => {
    if (isDemo) {
      setBusiness(prev => ({ ...prev, ...updates }));
    }
  };

  return (
    <AuthContext.Provider value={{
      session,
      business,
      subscriptionStatus: business?.subscription_status || 'inactive',
      subscriptionEndDate: business?.subscription_end_date || null,
      planType: business?.plan_type || null,
      needsMigration: !!(business?.paddle_user_id && !business?.polar_customer_id),
      isReturningUser: !!(
        (business?.polar_customer_id && business.polar_customer_id !== 'pending') ||
        business?.subscription_id
      ),
      isEmailConfirmed: true, // Email confirmation step disabled
      loading,
      signOut,
      isDemo,
      setDemoBusiness,
      refetchBusiness: async (userId) => {
        if (isDemo) return;
        let id = userId;
        if (!id) {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          id = currentSession?.user?.id;
        }
        if (id) return fetchBusiness(id);
      }
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
